// Уведомления о новой анкете карты «Рядом с домом» (/tutors/join).
//
// Чистые функции без сервера и базы — их читает scripts/verify-tutor-notify.mts.
// Два канала (письма админу о новой анкете нет с 19.10.2026):
//   1. строка в ленте «Уведомления» /admin — feedTitle();
//   2. счётчик непросмотренных на вкладке «Анкеты» — колонка
//      map_partners.admin_viewed_at (null = админ анкету ещё не смотрел).
// adminNotifyRecipient()/adminPartnerUrl() — для других писем админу («Правка
// анкеты», автоподбор).
//
// Пока миграция 20261017120000 не накатана, колонки нет: PostgREST отвечает
// 42703 (select/filter) или PGRST204 (update). isMissingViewedColumn() узнаёт
// эти ответы, и код молча работает без счётчика (fail-open по вёрстке).

import { labelOf, subjectItemsFor, type PartnerKind } from "@/data/map-taxonomy";

export const SITE = "https://yolutap.com";
export const NOTIFY_FALLBACK = "hello@yolutap.com";

/** Адресат писем админу — как у send-booking-notification.server.ts. */
export function adminNotifyRecipient(env: { NOTIFY_EMAIL?: string } = process.env): string {
  return env.NOTIFY_EMAIL || NOTIFY_FALLBACK;
}

/** Ссылка, которая открывает вкладку «Анкеты» и прокручивает к этой анкете. */
export function adminPartnerUrl(id: string): string {
  return `${SITE}/admin?tab=map-partners&partner=${encodeURIComponent(id)}`;
}

/** Тестовая анкета — имя начинается с «TEST» (решение владельца). */
export const isTestPartnerName = (name: string) => /^TEST/i.test(name.trim());

/** Подписи предметов по-русски. */
export function subjectLabelsRu(kind: PartnerKind, codes: string[]): string[] {
  const items = subjectItemsFor(kind);
  return codes.map((c) => labelOf(items, c, "ru"));
}

/** Первые 5 через запятую, остальное — «+N». */
export function shortSubjects(labels: string[], max = 5): string {
  if (labels.length <= max) return labels.join(", ");
  return `${labels.slice(0, max).join(", ")} +${labels.length - max}`;
}

export const kindWord = (kind: PartnerKind) => (kind === "tutor" ? "репетитора" : "кружка");

/** Строка ленты: «Новая анкета репетитора: <имя>, <предметы>». */
export function feedTitle(a: { kind: PartnerKind; name: string; subjects: string[] }): string {
  const test = isTestPartnerName(a.name) ? "[TEST] " : "";
  return `${test}Новая анкета ${kindWord(a.kind)}: ${a.name.trim()}, ${shortSubjects(subjectLabelsRu(a.kind, a.subjects))}`;
}

/** Ответ PostgREST «колонки admin_viewed_at нет» — до наката миграции. */
export function isMissingViewedColumn(
  error: { code?: string | null; message?: string | null } | null | undefined,
): boolean {
  if (!error) return false;
  if (error.code === "42703" || error.code === "PGRST204") return true;
  return /admin_viewed_at/.test(error.message ?? "");
}

/** Ответ PostgREST «колонки <column> нет» (42703 у select, PGRST204 у insert/update) — до наката. */
export function isMissingColumn(
  error: { code?: string | null; message?: string | null } | null | undefined,
  column: string,
): boolean {
  if (!error) return false;
  return (error.code === "42703" || error.code === "PGRST204") && (error.message ?? "").includes(column);
}

/** Счётчик вкладки: только непросмотренные (admin_viewed_at = null). */
export function countUnseen(rows: { admin_viewed_at?: string | null }[]): number {
  return rows.filter((r) => r.admin_viewed_at === null).length;
}
